import { CompleteKundali, Language } from '../types/kundali';
import { getSavByBhava } from './vedicEngine';
import {
  PersonalitySignature,
  LifeArea,
  LifeAreaDeepDiveResult,
  generatePersonalitySignature,
  generateLifeAreaDeepDive,
} from './integratedProfileGenerator';
import {
  AstrologerCoreViewModel,
  NativeCoreFacts,
  PlanetaryStrengthAxisFact,
  BhavaEvidenceItem,
  DivisionalChartEvidenceItem,
} from '../types/astrologerCore';
import { calculateShadbala, getBirthUtcDate } from './vedicEngine';
import { computeLifeStageContext } from './lifeStageContextEngine';

export const APPROVED_LIFE_AREAS: LifeArea[] = [
  'career',
  'wealth',
  'marriage',
  'progeny',
  'health',
  'property',
  'education',
  'foreignTravel',
  'spirituality',
];

export function buildAstrologerCoreViewModel(
  kundali: CompleteKundali,
  lang: Language = 'en'
): AstrologerCoreViewModel {
  const isHi = lang === 'hi';
  const personality = generatePersonalitySignature(kundali);

  // 1. Native Core Facts
  const lagnaHouse = kundali.houses.find((h) => h.houseNumber === 1);
  const lagnaSign = lagnaHouse?.sign || 'Aries';
  const lagnaLord = lagnaHouse?.signLord || 'Mars';

  const moonPlanet = kundali.planets.find((p) => p.name === 'Moon');
  const moonSign = moonPlanet?.sign || 'Aries';
  const nakshatra = kundali.panchang?.nakshatra || moonPlanet?.nakshatra || 'Ashwini';
  const pada = moonPlanet?.pada || 1;

  // Age calculation
  const now = new Date();
  let nativeAge: number | null = null;
  if (kundali.birthDetails?.date) {
    const bDate = new Date(kundali.birthDetails.date);
    if (!isNaN(bDate.getTime())) {
      let age = now.getFullYear() - bDate.getFullYear();
      const m = now.getMonth() - bDate.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < bDate.getDate())) {
        age--;
      }
      nativeAge = age >= 0 ? age : null;
    }
  }

  const isYoungChild = personality.isYoungChild || (nativeAge !== null && nativeAge < 11);

  // Active Vimshottari Dasha
  const currentDasha = kundali.dashas?.find((d) => d.isCurrent) || kundali.dashas?.[0];
  const activeAntardasha =
    currentDasha?.antardashas?.find((ad) => {
      const start = new Date(ad.startDate).getTime();
      const end = new Date(ad.endDate).getTime();
      const current = now.getTime();
      return current >= start && current <= end;
    }) || currentDasha?.antardashas?.[0];

  const currentMahadasha = currentDasha?.planet || 'Jupiter';
  const currentAntardashaName = activeAntardasha?.planet || 'Jupiter';
  const dashaDateRange = currentDasha
    ? `${currentDasha.startDate} — ${currentDasha.endDate}`
    : 'Active Period';

  const nativeCore: NativeCoreFacts = {
    lagnaSign,
    lagnaLord,
    moonSign,
    nakshatra,
    pada,
    nativeAge,
    currentMahadasha,
    currentAntardasha: currentAntardashaName,
    dashaDateRange,
    isYoungChild,
  };

  // 2. Planetary Strength Axis (Reusing Shadbala calculation & PersonalitySignature)
  const shadbalaResult = kundali.shadbala || calculateShadbala(
    kundali.charts.D1?.planets || kundali.planets,
    kundali.houses,
    {
      birthDate: getBirthUtcDate(kundali.birthDetails),
      latitude: kundali.birthDetails.latitude,
      longitude: kundali.birthDetails.longitude
    }
  );
  const sortedPlanets = [...shadbalaResult.planets].sort(
    (a, b) => b.totalRupas - a.totalRupas
  );
  const strongestShadbala = sortedPlanets[0] || { planet: 'Jupiter', totalRupas: 7.5 };
  const weakestShadbala =
    sortedPlanets[sortedPlanets.length - 1] || { planet: 'Saturn', totalRupas: 4.8 };

  const strengthAxis: PlanetaryStrengthAxisFact = {
    strongest: {
      planet: personality.dominantExecutiveEngine.planet,
      rupas: personality.dominantExecutiveEngine.rupas,
      strengthDescriptionEn: `${personality.dominantExecutiveEngine.planet} is the primary functional engine (${personality.dominantExecutiveEngine.rupas} Rupas), granting natural executive thrust and instinctive capability.`,
      strengthDescriptionHi: `${personality.dominantExecutiveEngine.planet} प्राथमिक कार्य-संचालक ग्रह है (${personality.dominantExecutiveEngine.rupas} रूप), जो स्वाभाविक संकल्पशक्ति और कार्यक्षमता प्रदान करता है।`,
      overusePatternEn: personality.dominantExecutiveEngine.strengthOveruseEn,
      overusePatternHi: personality.dominantExecutiveEngine.strengthOveruseHi,
    },
    weakest: {
      planet: personality.fundamentalVulnerability.planet,
      rupas: personality.fundamentalVulnerability.rupas,
      vulnerabilityEn: personality.fundamentalVulnerability.blindspotEn,
      vulnerabilityHi: personality.fundamentalVulnerability.blindspotHi,
      correctiveFocusEn: `Consciously build deliberate checkpoints and structured habits to support ${personality.fundamentalVulnerability.planet} domains.`,
      correctiveFocusHi: `${personality.fundamentalVulnerability.planet} से जुड़े क्षेत्रों में सजगता और नियमित अनुशासन से संतुलन बनाएं।`,
    },
  };

  // 3. Polarity
  const polarity = {
    outerVesselEn: personality.lagnaArchetype.presenceEn,
    outerVesselHi: personality.lagnaArchetype.presenceHi,
    innerCoreEn: personality.mindArchetype.cognitiveStyleEn,
    innerCoreHi: personality.mindArchetype.cognitiveStyleHi,
    corePolarityEn: personality.corePolarityDynamic.polarityEn,
    corePolarityHi: personality.corePolarityDynamic.polarityHi,
  };

  // 4. Timing vs. Inherent Capacity
  // Find activated houses by Mahadasha lord
  const activatedHouses: number[] = [];
  kundali.houses.forEach((h) => {
    if (h.signLord === currentMahadasha) {
      activatedHouses.push(h.houseNumber);
    }
  });

  const timing = {
    inherentCapacitySummaryEn: `Inherent matrix determined by Lagna (${lagnaSign}), Moon (${moonSign} / ${nakshatra}), Shadbala axis (${personality.dominantExecutiveEngine.planet} vs ${personality.fundamentalVulnerability.planet}), and Ashtakvarga baseline.`,
    inherentCapacitySummaryHi: `जन्मजात संरचना लग्न (${lagnaSign}), चंद्रमा (${moonSign} / ${nakshatra}), षड्बल अक्ष (${personality.dominantExecutiveEngine.planet} बनाम ${personality.fundamentalVulnerability.planet}) तथा अष्टकवर्ग आधार पर आधारित है।`,
    activeTimingSummaryEn: `Active time-window governed by ${currentMahadasha} Mahadasha (Antardasha: ${currentAntardashaName}). Stimulates Houses ${activatedHouses.length > 0 ? activatedHouses.join(', ') : '1'} in life-area manifestation.`,
    activeTimingSummaryHi: `वर्तमान काल-प्रवाह ${currentMahadasha} महादशा (अंतर्दशा: ${currentAntardashaName}) द्वारा संचालित है, जो भाव ${activatedHouses.length > 0 ? activatedHouses.join(', ') : '1'} को सक्रिय करता है।`,
    mahadasha: currentMahadasha,
    antardasha: currentAntardashaName,
    activatedHouses,
  };

  // 5. Age Maturation
  const ageMaturation = {
    maturationArcEn: personality.maturationArc.arcEn,
    maturationArcHi: personality.maturationArc.arcHi,
    isYoungChild,
  };

  // 6. Astrological Core Evidence
  const bhavas: BhavaEvidenceItem[] = (kundali.houses || []).map((h) => {
    const occupants = (kundali.planets || [])
      .filter((p) => p.house === h.houseNumber)
      .map((p) => p.name);
    const sav = getSavByBhava(kundali.ashtakvarga, h.houseNumber, kundali.houses);
    const isKendra = [1, 4, 7, 10].includes(h.houseNumber);
    const isTrikona = [1, 5, 9].includes(h.houseNumber);
    const isDusthana = [6, 8, 12].includes(h.houseNumber);

    return {
      houseNumber: h.houseNumber,
      sign: h.sign,
      signLord: h.signLord,
      occupants,
      savBindus: sav,
      isKendra,
      isTrikona,
      isDusthana,
    };
  });

  const planetaryStrengths = sortedPlanets.map((p) => ({
    planet: p.planet,
    rupas: p.totalRupas,
    category: p.strengthCategory,
    isStrongest: p.planet === strongestShadbala.planet,
    isWeakest: p.planet === weakestShadbala.planet,
  }));

  const charaKarakas = (kundali.charaKarakas || []).map((ck) => ({
    code: ck.code,
    name: ck.name,
    planet: ck.planet,
    role: ck.role,
  }));

  // Divisional chart audit: distinguish live calculated data from metadata
  const divisionalCharts: DivisionalChartEvidenceItem[] = [
    {
      code: 'D1',
      title: isHi ? 'राशि चक्र (लग्न कुंडली)' : 'Rashi Chart (D1)',
      purpose: isHi ? 'भौतिक अस्तित्व, देह एवं संपूर्ण जीवन आधार' : 'Physical reality, vessel & complete baseline',
      isLiveCalculated: !!kundali.charts?.D1,
      statusTextEn: kundali.charts?.D1 ? 'Live Calculated' : 'Available',
      statusTextHi: kundali.charts?.D1 ? 'सक्रिय गणना उपलब्ध' : 'उपलब्ध',
      keyPlacementEn: `Ascendant: ${lagnaSign} | Lagna Lord: ${lagnaLord}`,
      keyPlacementHi: `लग्न: ${lagnaSign} | लग्नेश: ${lagnaLord}`,
    },
    {
      code: 'D9',
      title: isHi ? 'नवांश चक्र (D9)' : 'Navamsha Chart (D9)',
      purpose: isHi ? 'आत्मिक शक्ति, दांपत्य एवं भाग्य का सूक्ष्म आधार' : 'Inner soul strength, dharma & partnership durability',
      isLiveCalculated: !!kundali.charts?.D9,
      statusTextEn: kundali.charts?.D9 ? 'Live Calculated' : 'Available',
      statusTextHi: kundali.charts?.D9 ? 'सक्रिय गणना उपलब्ध' : 'उपलब्ध',
      keyPlacementEn: kundali.charts?.D9 ? `Navamsha Lagna / Moon: ${kundali.charts.D9.planets?.find((p) => p.name === 'Moon')?.sign || 'Active'}` : 'Calculated in engine',
      keyPlacementHi: kundali.charts?.D9 ? `नवांश चंद्रमा: ${kundali.charts.D9.planets?.find((p) => p.name === 'Moon')?.sign || 'सक्रिय'}` : 'इंजन में परिकलित',
    },
    {
      code: 'D10',
      title: isHi ? 'दशमांश चक्र (D10)' : 'Dashamsha Chart (D10)',
      purpose: isHi ? 'कर्मक्षेत्र, पद-प्रतिष्ठा एवं आजीविका' : 'Career authority, public status & professional impact',
      isLiveCalculated: !!kundali.charts?.D10,
      statusTextEn: kundali.charts?.D10 ? 'Live Calculated' : 'Available',
      statusTextHi: kundali.charts?.D10 ? 'सक्रिय गणना उपलब्ध' : 'उपलब्ध',
      keyPlacementEn: kundali.charts?.D10 ? `10th Lord placement evaluated` : 'Calculated in engine',
      keyPlacementHi: kundali.charts?.D10 ? `दशम भाव कारक स्थिति विश्लेषित` : 'इंजन में परिकलित',
    },
    {
      code: 'D7',
      title: isHi ? 'सप्तमांश चक्र (D7)' : 'Saptamsha Chart (D7)',
      purpose: isHi ? 'संतान, सृजनशीलता एवं वंश वृद्धि' : 'Progeny, lineage continuity & creative fruitfulness',
      isLiveCalculated: !!kundali.charts?.D7,
      statusTextEn: kundali.charts?.D7 ? 'Live Calculated' : 'Available',
      statusTextHi: kundali.charts?.D7 ? 'सक्रिय गणना उपलब्ध' : 'उपलब्ध',
      keyPlacementEn: kundali.charts?.D7 ? `5th Lord and Jupiter evaluated` : 'Calculated in engine',
      keyPlacementHi: kundali.charts?.D7 ? `पंचमेश एवं गुरु स्थिति विश्लेषित` : 'इंजन में परिकलित',
    },
    {
      code: 'D24',
      title: isHi ? 'सिद्धांश चक्र (D24)' : 'Siddhamsha Chart (D24)',
      purpose: isHi ? 'विद्या, अनुसंधान, उच्च शिक्षा एवं प्रज्ञा' : 'Learning, scholarship, higher intelligence & education',
      isLiveCalculated: !!kundali.charts?.D24,
      statusTextEn: kundali.charts?.D24 ? 'Live Calculated' : 'Available',
      statusTextHi: kundali.charts?.D24 ? 'सक्रिय गणना उपलब्ध' : 'उपलब्ध',
      keyPlacementEn: kundali.charts?.D24 ? `Mercury/Jupiter education baseline` : 'Calculated in engine',
      keyPlacementHi: kundali.charts?.D24 ? `बुध एवं गुरु शैक्षणिक स्थिति` : 'इंजन में परिकलित',
    },
    {
      code: 'D60',
      title: isHi ? 'षष्ट्यंश चक्र (D60)' : 'Shashtiamsha Chart (D60)',
      purpose: isHi ? 'पूर्वजन्म के संचित कर्म एवं सूक्ष्म नियति' : 'Past life karma & subtle destiny root',
      isLiveCalculated: !!kundali.charts?.D60,
      statusTextEn: kundali.charts?.D60 ? 'Live Calculated' : 'Architectural Metadata',
      statusTextHi: kundali.charts?.D60 ? 'सक्रिय गणना उपलब्ध' : 'सैद्धांतिक संदर्भ (सीधा साक्ष्य नहीं)',
      keyPlacementEn: kundali.charts?.D60 ? 'D60 Karma active' : 'Not currently available as direct live evidence.',
      keyPlacementHi: kundali.charts?.D60 ? 'D60 कर्म सक्रिय' : 'वर्तमान में प्रत्यक्ष साक्ष्य के रूप में उपलब्ध नहीं है।',
    },
  ];

  // 7. Life Area Deep Dives for all 9 approved areas
  const lifeAreaDeepDives: Record<LifeArea, LifeAreaDeepDiveResult> = {} as any;
  APPROVED_LIFE_AREAS.forEach((area) => {
    lifeAreaDeepDives[area] = generateLifeAreaDeepDive(area, personality, kundali, lang);
  });

  const lifeStageContext = computeLifeStageContext(kundali);

  return {
    nativeCore,
    personalitySignature: personality,
    strengthAxis,
    polarity,
    timing,
    ageMaturation,
    lifeStageContext,
    evidence: {
      bhavas,
      planetaryStrengths,
      charaKarakas,
      divisionalCharts,
    },
    lifeAreaDeepDives,
  };
}
